import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import TransactionResultPopup from '../TransactionResultPopup';
import ProposalManager from './ProposalManager';
import DelegationManager from './DelegationManager';

const GovernancePanel = ({ 
  fundContract, 
  governanceContract, 
  yildxTokenContract, 
  account, 
  setError, 
  setSuccess, 
  setLoading, 
  loading, 
  proposalRedirect, 
  setProposalRedirect,
  isAdmin,
  isOperator,
  onClaimFees
}) => {
  // State for user governance info
  const [votingPower, setVotingPower] = useState('0');
  const [canPropose, setCanPropose] = useState(false);
  const [unclaimedFees, setUnclaimedFees] = useState('0');
  const [yildxBalance, setYildxBalance] = useState('0');
  
  // State for operator management
  const [currentOperator, setCurrentOperator] = useState(null);
  const [showOperatorManagement, setShowOperatorManagement] = useState(false);
  
  // Transaction popup state
  const [showTxPopup, setShowTxPopup] = useState(false);
  const [txStatus, setTxStatus] = useState('pending'); // 'pending', 'success', 'failed', 'cancelled'
  const [txHash, setTxHash] = useState('');
  const [txMessage, setTxMessage] = useState('');
  const [txProvider, setTxProvider] = useState(null);
  
  // Make sure to grab the provider if available
  useEffect(() => {
    if (yildxTokenContract && yildxTokenContract.provider) {
      setTxProvider(yildxTokenContract.provider);
    }
  }, [yildxTokenContract]);

  // Load current operator information
  useEffect(() => {
    const loadOperatorInfo = async () => {
      if (!fundContract || !account) return;
      
      try {
        // Check if the authorizedOperators function exists and use it directly
        if (typeof fundContract.authorizedOperators === 'function') {
          console.log("Checking if account is an operator using authorizedOperators...");
          
          // First check if current account is an operator
          const isCurrentAccountOperator = await fundContract.authorizedOperators(account);
          if (isCurrentAccountOperator) {
            console.log(`Current account (${account}) is an operator`);
            
            // Get operator token balance
            const balance = await yildxTokenContract.balanceOf(account);
            
            setCurrentOperator({
              address: account,
              balance: ethers.utils.formatEther(balance),
            });
            return;
          }
          
          // If the contract has an owner, check if the owner is an operator
          try {
            const owner = await fundContract.owner();
            if (owner) {
              const isOwnerOperator = await fundContract.authorizedOperators(owner);
              if (isOwnerOperator) {
                console.log(`Fund owner (${owner}) is an operator`);
                
                // Get operator token balance
                const balance = await yildxTokenContract.balanceOf(owner);
                
                setCurrentOperator({
                  address: owner,
                  balance: ethers.utils.formatEther(balance),
                });
                return;
              }
            }
          } catch (ownerError) {
            console.log("Error checking if owner is an operator:", ownerError.message);
          }
          
          // If we still don't have an operator, attempt to find another operator
          console.log("Checking if any known addresses are operators...");
          
          // Only try these addresses if default ones don't work
          const potentialOperators = [
            // Add any additional addresses you want to check
            "0x70997970C51812dc3A010C7d01b50e0d17dc79C8", // From your test script
            "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266", // First Hardhat account 
            "0x3C44CdDdB6a900fa2b585dd299e03d12FA4293BC"  // Another Hardhat account
          ];
          
          for (const addr of potentialOperators) {
            try {
              const isOperator = await fundContract.authorizedOperators(addr);
              if (isOperator) {
                console.log(`Found operator at address: ${addr}`);
                
                // Get operator token balance
                const balance = await yildxTokenContract.balanceOf(addr);
                
                setCurrentOperator({
                  address: addr,
                  balance: ethers.utils.formatEther(balance),
                });
                return;
              }
            } catch (error) {
              console.log(`Error checking if ${addr} is an operator:`, error.message);
            }
          }
          
          console.log("No operators found through authorizedOperators checks");
        } else {
          console.log("authorizedOperators function not available on contract");
        }
        
        // If no operator was found, display a message
        console.log("No operator found or configured");
      } catch (error) {
        console.error("Error loading operator info:", error);
      }
    };
    
    // Load operator info on component mount
    loadOperatorInfo();
  }, [fundContract, account, yildxTokenContract]);

  // Load user's governance data
  const loadUserGovernanceData = async () => {
    try {
      // Get YILDX token balance
      const balance = await yildxTokenContract.balanceOf(account);
      setYildxBalance(ethers.utils.formatEther(balance));
      
      // Check if user can create proposals (more than 100 YILDX)
      const MIN_TOKENS_TO_VOTE = ethers.utils.parseEther("100"); // 100 YILDX tokens
      setCanPropose(balance.gte(MIN_TOKENS_TO_VOTE));
      
      // Get voting power
      const votingPower = await governanceContract.getVotingPower(account);
      setVotingPower(ethers.utils.formatEther(votingPower));
      
      // Get unclaimed fees
      const fees = await yildxTokenContract.pendingFees(account);
      setUnclaimedFees(ethers.utils.formatEther(fees));
      
    } catch (error) {
      console.error('Error loading user governance data:', error);
    }
  };

  useEffect(() => {
    if (governanceContract && yildxTokenContract && account) {
      loadUserGovernanceData();
    }
  }, [governanceContract, yildxTokenContract, account]);
  
  // Claim Fees function
  const handleClaimFees = async () => {
    if (!yildxTokenContract) {
      setError("YILDX Token contract not available");
      return;
    }
    
    setLoading(true);
    
    try {
      console.log("GovernancePanel: Claiming fees...");
      
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage('Your claim fees transaction is being processed on the blockchain.');
      setShowTxPopup(true);
      
      // Use the provided handler if available, otherwise use direct contract call
      if (typeof onClaimFees === 'function') {
        await onClaimFees();
        // We don't have access to the tx hash here as the parent component handles it
        // So we'll just update the UI based on the result
        setTxStatus('success');
        setTxMessage('Successfully claimed governance fees');
      } else {
        const tx = await yildxTokenContract.claimFees();
        console.log("GovernancePanel: Fee claim transaction submitted:", tx.hash);
        
        // Update popup with hash
        setTxHash(tx.hash);
        
        // Wait for transaction to be mined
        const receipt = await tx.wait();
        console.log("GovernancePanel: Fee claim transaction mined:", receipt);
        
        // Update popup status
        setTxStatus('success');
        setTxMessage('Successfully claimed governance fees');
      }
      
      // Refresh user's governance data to update unclaimed fees
      await loadUserGovernanceData();
      
      setSuccess("Successfully claimed governance fees");
    } catch (err) {
      console.error('Error claiming fees:', err);
      setError(err.message || 'Failed to claim fees');
      
      // Check if the error is due to user rejecting the transaction
      if (err.code === 4001 || (err.message && err.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to claim fees: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Function to handle popup close
  const handleTxPopupClose = () => {
    setShowTxPopup(false);
  };
  
  // Function to handle manual refresh of transaction status
  const handleTxRefresh = async () => {
    await loadUserGovernanceData();
  };
  
  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };
  
  return (
    <div className="bg-white rounded-lg border p-6">
      <h2 className="text-xl font-semibold mb-4">Governance Dashboard</h2>
      
      {/* User Governance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-purple-50 rounded-lg">
          <div className="text-sm text-purple-600">YILDX Balance</div>
          <div className="text-xl font-semibold">{parseFloat(yildxBalance).toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
          <div className="text-xs text-gray-500">
            {parseFloat(yildxBalance) >= 100 ? 'Can create & vote on proposals' : 'Need 100 YILDX tokens to create proposals'}
          </div>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-600">Voting Power</div>
          <div className="text-xl font-semibold">{parseFloat(votingPower).toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
        </div>
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex flex-col h-full">
            <div className="text-sm text-green-600">Unclaimed Fees</div>
            <div className="text-xl font-semibold">{parseFloat(unclaimedFees).toFixed(6)} ETH</div>
            <div className="text-xs text-gray-500 mb-2">
              Fees are distributed when users mint or redeem tokens
            </div>
            {parseFloat(unclaimedFees) > 0 && (
              <button
                className="mt-auto bg-green-600 hover:bg-green-700 text-white py-1 px-3 rounded text-sm"
                onClick={handleClaimFees}
                disabled={loading || parseFloat(unclaimedFees) <= 0}
              >
                {loading ? 'Processing...' : 'Claim Fees'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Operator Management Section */}
      <div className="mb-6 p-4 bg-amber-50 rounded-lg">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-amber-800">Operator Management</h3>
          <button 
            className="px-3 py-1 bg-amber-600 text-white rounded-md text-sm hover:bg-amber-700"
            onClick={() => setShowOperatorManagement(!showOperatorManagement)}
          >
            {showOperatorManagement ? 'Hide Operator Options' : 'Manage Operators'}
          </button>
        </div>
        
        {/* Current Operator Info */}
        {currentOperator && (
          <div className="mb-4 p-3 bg-white rounded-md shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium">Current Operator:</div>
                <div className="text-md text-amber-700">{currentOperator.address}</div>
                <div className="text-xs text-gray-600 mt-1">
                  <span className="font-medium">YILDX Balance:</span> {parseFloat(currentOperator.balance).toLocaleString()} YILDX
                </div>
              </div>
              <div className="flex items-center">
                {isAdmin && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Admin can modify directly</span>
                )}
                {isOperator && currentOperator.address.toLowerCase() === account.toLowerCase() && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs ml-2">You are the operator</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Delegation Management Component */}
      <DelegationManager 
        governanceContract={governanceContract}
        account={account}
        setError={setError}
        setSuccess={setSuccess}
        setLoading={setLoading}
        loading={loading}
        loadUserGovernanceData={loadUserGovernanceData}
      />
      
      {/* Proposal Management Component */}
      <ProposalManager
        fundContract={fundContract}
        governanceContract={governanceContract}
        yildxTokenContract={yildxTokenContract}
        account={account}
        setError={setError}
        setSuccess={setSuccess}
        setLoading={setLoading}
        loading={loading}
        proposalRedirect={proposalRedirect}
        setProposalRedirect={setProposalRedirect}
        canPropose={canPropose}
        currentOperator={currentOperator}
        showOperatorManagement={showOperatorManagement}
        formatAddress={formatAddress}
        setTxStatus={setTxStatus}
        setTxHash={setTxHash}
        setTxMessage={setTxMessage}
        setShowTxPopup={setShowTxPopup}
      />
      
      {/* Information section */}
      <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
        <h4 className="font-bold">About Token-Based Governance</h4>
        <ul className="list-disc ml-5 mt-1 text-sm">
          <li className="mb-1">Governance is managed through YILDX tokens. 100 YILDX tokens are required to create proposals.</li>
          <li className="mb-1">All YILDX holders can vote on proposals, with voting power equal to their token balance.</li>
          <li className="mb-1">You can delegate your voting power to another address while keeping your tokens.</li>
          <li className="mb-1">A proposal needs to have more 'for' votes than 'against' votes to pass.</li>
          <li className="mb-1">Proposals have a voting period, followed by an execution delay.</li>
          <li className="mb-1">Proposals that reach a strong majority can be executed immediately without waiting.</li>
          <li className="mb-1">Operators can be added or removed through governance proposals that require a majority vote.</li>
          <li>Fees collected by the fund are distributed to YILDX token holders.</li>
        </ul>
      </div>
      
      {/* Transaction Result Popup */}
      <TransactionResultPopup
        isOpen={showTxPopup}
        onClose={handleTxPopupClose}
        onRefresh={handleTxRefresh}
        status={txStatus}
        actionType="Claim Fees"
        amount={unclaimedFees}
        message={txMessage}
        txHash={txHash}
        provider={txProvider}
      />
    </div>
  );
};

export default GovernancePanel;